import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetTicketRiskReport } from '~/hooks/apis/support/useTicketRiskReport';
import { useWatchTicketRisk } from '~/hooks/apis/support/useTicketRiskWatch';
import useCurrentUser from '~/hooks/useCurrentUser';

import { canAccessCxDashboard } from '~/utils/canAccessCxDashboard';

import TicketDetailDialog from './TicketDetailDialog';
import { CX_DEFAULT_HIDDEN_COLUMN_FIELDS, buildCxTicketColumnGroups } from './cxTicketColumns';
import {
  type CxTicketRow,
  INTERNAL_COMMENT_SIGNAL_THRESHOLD,
  JOURNEY_PHASES,
  REASSIGNMENT_SIGNAL_THRESHOLD,
  REOPEN_SIGNAL_THRESHOLD,
  RISK_TIERS,
  type RiskTier,
  SEVEN_DAYS_IN_MINUTES,
  TIME_OPEN_FILTER_DAYS,
  type TimeOpenFilter,
  formatAiSignalLabel,
  formatDateTime,
  formatShortDateTime,
  getAiSignalTokens,
  isEmergingSignal,
  joinDetails,
  parseRiskTierParam,
  parseTimeOpenParam,
  toCxTicketRows,
} from './ticketRiskRows';

const ALL = 'All';

const TEAM_MENU_MAX_HEIGHT = 10 * 48 + 8;
const COLUMN_MENU_MAX_HEIGHT = 15 * 48 + 8;
const DASHBOARD_PANEL_HEIGHT = 340;
/** Two lines of content plus padding — the triage columns stack a value over its detail. */
const COMPRESSED_ROW_HEIGHT = 64;
const MAX_RISK_DRIVERS = 5;
const MAX_CATEGORY_TREND_ROWS = 6;
const MAX_TEAMS_CHARTED = 6;
type CxSignalFilter =
  | 'New'
  | 'Unwatched'
  | 'WatchedByMe'
  | 'Negative'
  | 'Complaint'
  | 'Reassigned'
  | 'CustomerWaiting'
  | 'InternalComments'
  | 'Reopened'
  | 'AuthorityMentioned';
type RiskDriverType = 'Rule' | 'AI signal' | 'Emerging';

const SIGNAL_FILTER_OPTIONS: Array<{ id: CxSignalFilter; label: string }> = [
  { id: 'New', label: 'New' },
  { id: 'Unwatched', label: 'Unwatched' },
  { id: 'WatchedByMe', label: 'Watched by me' },
  { id: 'Negative', label: 'Negative Sentiment' },
  { id: 'Complaint', label: 'Complaint' },
  { id: 'Reassigned', label: 'Reassigned 3+' },
  { id: 'CustomerWaiting', label: 'Customer waiting 7+ days' },
  { id: 'InternalComments', label: 'Internal Comments 10+' },
  { id: 'Reopened', label: 'Reopened 2+' },
  { id: 'AuthorityMentioned', label: 'Authority Mentioned' },
];

const matchesTimeOpenFilter = (row: CxTicketRow, minimumDays: TimeOpenFilter | null) =>
  minimumDays === null || row.caseAgeDays >= minimumDays;

const SIGNAL_FILTER_LABELS = Object.fromEntries(
  SIGNAL_FILTER_OPTIONS.map((option) => [option.id, option.label]),
) as Record<CxSignalFilter, string>;

const parseSelectValues = <T extends string>(value: string | Array<T>): Array<T> =>
  typeof value === 'string' ? (value.split(',') as Array<T>) : value;

const matchesSelectedValues = (selected: Array<string>, value: string | null | undefined): boolean =>
  selected.length === 0 || (value != null && selected.includes(value));

const matchesTeamFilter = (selected: Array<string>, team: string | null | undefined): boolean => {
  if (selected.length === 0) {
    return true;
  }

  return selected.some((item) => (item === 'Unassigned' ? !team : team === item));
};

const toggleListValue = <T,>(current: Array<T>, value: T): Array<T> =>
  current.includes(value) ? current.filter((item) => item !== value) : [...current, value];

function MultiFilterSelect<T extends string>({
  id,
  label,
  value,
  options,
  optionLabels,
  minWidth,
  onChange,
}: {
  id: string;
  label: string;
  value: Array<T>;
  options: Array<T>;
  optionLabels?: Partial<Record<T, string>>;
  minWidth: number;
  onChange: (value: Array<T>) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={id} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={id}
        multiple
        displayEmpty
        notched
        value={value}
        label={label}
        MenuProps={{ PaperProps: { sx: { maxHeight: TEAM_MENU_MAX_HEIGHT } } }}
        onChange={(event) => onChange(parseSelectValues<T>(event.target.value))}
        renderValue={(selected) =>
          selected.length === 0 ? 'All' : selected.map((item) => optionLabels?.[item] ?? item).join(', ')
        }
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={value.includes(option)} />
            <ListItemText primary={optionLabels?.[option] ?? option} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const matchesSignalFilter = (row: CxTicketRow, signalFilters: Array<CxSignalFilter>) => {
  if (signalFilters.length === 0) {
    return true;
  }

  return signalFilters.some(
    (signalFilter) =>
      (signalFilter === 'New' && row.isNew) ||
      (signalFilter === 'Unwatched' && row.watcher === null) ||
      (signalFilter === 'WatchedByMe' && row.isWatchedByMe) ||
      (signalFilter === 'Negative' && row.sentimentBucket === 'Negative') ||
      (signalFilter === 'Complaint' && row.ticketReason?.trim().toLowerCase() === 'complaint') ||
      (signalFilter === 'Reassigned' && row.reassignCount >= REASSIGNMENT_SIGNAL_THRESHOLD) ||
      (signalFilter === 'CustomerWaiting' && row.requesterWaitMinutes >= SEVEN_DAYS_IN_MINUTES) ||
      (signalFilter === 'InternalComments' && row.internalComments >= INTERNAL_COMMENT_SIGNAL_THRESHOLD) ||
      (signalFilter === 'Reopened' && row.reopenCount >= REOPEN_SIGNAL_THRESHOLD) ||
      (signalFilter === 'AuthorityMentioned' && row.authorityConfirmed),
  );
};

const UNCATEGORISED_LABEL = 'Uncategorised';
const AT_RISK_TIERS: Array<RiskTier> = ['Critical', 'High'];

const getCategoryLabel = (row: CxTicketRow) => {
  const values = [row.ticketReason, row.subcategory].filter((value): value is string => Boolean(value));

  return values.length > 0 ? values.join(' · ') : UNCATEGORISED_LABEL;
};

/** Any of the selected signals matching is enough, as with the rule-based Signal filter. */
const matchesAiSignalFilter = (row: CxTicketRow, selectedSignals: Array<string>) =>
  selectedSignals.length === 0 || getAiSignalTokens(row).some((token) => selectedSignals.includes(token));

interface RiskDriver {
  label: string;
  count: number;
  scoreTotal: number;
  type: RiskDriverType;
  query: string;
  averageScore: number;
}

/**
 * Ranks what is driving risk across a set of tickets from two angles: the rule-based category
 * (ticket reason + subcategory) and the scorer's own language signals.
 */
const buildRiskDrivers = (rows: Array<CxTicketRow>): Array<RiskDriver> => {
  type DriverTally = { count: number; scoreTotal: number; type: RiskDriverType; query: string };
  const ruleDrivers = new Map<string, DriverTally>();
  const aiDrivers = new Map<string, DriverTally>();

  rows.forEach((row) => {
    const category = getCategoryLabel(row);
    // Absence of a classification is not a risk signal, and it dominated the Low tier.
    if (category !== UNCATEGORISED_LABEL) {
      const ruleDriver = ruleDrivers.get(category) ?? { count: 0, scoreTotal: 0, type: 'Rule', query: category };
      ruleDriver.count += 1;
      ruleDriver.scoreTotal += row.riskScore;
      ruleDrivers.set(category, ruleDriver);
    }

    getAiSignalTokens(row).forEach((token) => {
      const aiDriver = aiDrivers.get(token) ?? {
        count: 0,
        scoreTotal: 0,
        // "other:<name>" is the scorer's escape hatch for a pattern outside its vocabulary.
        type: isEmergingSignal(token) ? 'Emerging' : 'AI signal',
        query: token,
      };
      aiDriver.count += 1;
      aiDriver.scoreTotal += row.riskScore;
      aiDrivers.set(token, aiDriver);
    });
  });

  const ranked = [...ruleDrivers.entries(), ...aiDrivers.entries()]
    .map(([label, driver]) => ({
      label: driver.type === 'Rule' ? label : formatAiSignalLabel(label),
      ...driver,
      averageScore: driver.count === 0 ? 0 : driver.scoreTotal / driver.count,
    }))
    .sort((left, right) => right.count - left.count || right.averageScore - left.averageScore);

  const top = ranked.slice(0, MAX_RISK_DRIVERS);
  // An emerging signal is the newest and smallest by definition, so it loses the count
  // sort and would stay hidden until it grew — the opposite of an early warning.
  if (top.length === MAX_RISK_DRIVERS && !top.some((driver) => driver.type === 'Emerging')) {
    const leadingEmerging = ranked.find((driver) => driver.type === 'Emerging');
    if (leadingEmerging) {
      top[MAX_RISK_DRIVERS - 1] = leadingEmerging;
    }
  }

  return top;
};

export default function CxDashboardPage() {
  // Deep links (the daily aging email) seed the filters once; the controls own them from then on.
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [aiSignalFilter, setAiSignalFilter] = useState<Array<string>>([]);
  const [statusFilter, setStatusFilter] = useState<Array<string>>([]);
  const [riskTierFilter, setRiskTierFilter] = useState<Array<RiskTier>>(() =>
    parseRiskTierParam(searchParams.get('riskTier')),
  );
  const [riskSignalsTier, setRiskSignalsTier] = useState<RiskTier | null>(null);
  const [teamFilter, setTeamFilter] = useState<Array<string>>([]);
  const [teamLeaderFilter, setTeamLeaderFilter] = useState<Array<string>>([]);
  const [journeyPhaseFilter, setJourneyPhaseFilter] = useState<Array<string>>([]);
  const [timeOpenFilter, setTimeOpenFilter] = useState<TimeOpenFilter | null>(() =>
    parseTimeOpenParam(searchParams.get('timeOpen')),
  );
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [signalFilters, setSignalFilters] = useState<Array<CxSignalFilter>>([]);
  const [hiddenColumnFields, setHiddenColumnFields] = useState<Array<string>>(CX_DEFAULT_HIDDEN_COLUMN_FIELDS);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const theme = useTheme();

  const { user } = useCurrentUser();
  const hasAccess = canAccessCxDashboard(user);

  const { data: report, isLoading, isFetching, error, refetch } = useGetTicketRiskReport({ enabled: hasAccess });
  const watchTicket = useWatchTicketRisk();

  const rows = useMemo(() => toCxTicketRows(report, user?.id_member ?? null), [report, user?.id_member]);
  // Resolved from the live rows rather than stored as a snapshot, so the dialog reflects a watch
  // the moment the report cache is patched.
  const activeTicket = useMemo(() => rows.find((row) => row.id === activeTicketId) ?? null, [rows, activeTicketId]);

  const statusOptions = useMemo(() => Array.from(new Set(rows.map((row) => row.ticketStatus))).sort(), [rows]);
  const teamOptions = useMemo(() => {
    const teams = Array.from(new Set(rows.map((row) => row.team).filter((team): team is string => !!team))).sort();
    const hasUnassigned = rows.some((row) => !row.team);

    return hasUnassigned ? ['Unassigned', ...teams] : teams;
  }, [rows]);
  const teamLeaderOptions = useMemo(() => {
    const teamLeaders = Array.from(
      new Set(rows.map((row) => row.teamLeader).filter((teamLeader): teamLeader is string => !!teamLeader)),
    ).sort();
    const hasUnassigned = rows.some((row) => !row.teamLeader);

    return hasUnassigned ? ['Unassigned', ...teamLeaders] : teamLeaders;
  }, [rows]);
  const riskTierOptions = [...RISK_TIERS];
  const journeyPhaseOptions = [...JOURNEY_PHASES];
  /**
   * Only the signals this run actually produced, commonest first — the scorer's vocabulary runs
   * to well over a hundred names, and an alphabetical list of all of them would bury the ones
   * worth filtering on.
   */
  const aiSignalOptions = useMemo(() => {
    const counts = new Map<string, number>();

    rows.forEach((row) => {
      getAiSignalTokens(row).forEach((token) => counts.set(token, (counts.get(token) ?? 0) + 1));
    });

    return Array.from(counts.entries())
      .sort(
        ([leftToken, leftCount], [rightToken, rightCount]) =>
          rightCount - leftCount || leftToken.localeCompare(rightToken),
      )
      .map(([token]) => token);
  }, [rows]);
  const aiSignalLabels = useMemo(
    () =>
      Object.fromEntries(
        aiSignalOptions.map((token) => [
          token,
          // Emerging names are the scorer's own coinages, so they are worth marking in the list.
          isEmergingSignal(token) ? `${formatAiSignalLabel(token)} (emerging)` : formatAiSignalLabel(token),
        ]),
      ) as Record<string, string>,
    [aiSignalOptions],
  );

  const rowsBeforeCategoryFilter = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !normalizedSearchTerm ||
        [
          row.id,
          row.subject,
          row.team,
          row.assignee,
          row.teamLeader,
          row.ticketStatus,
          row.ticketReason,
          row.subcategory,
          row.triageCategory,
          row.journeyPhase,
          row.riskTier,
          row.sentiment,
          row.societeTier,
          row.llmTrajectory,
          ...row.llmRiskFactors,
          ...row.llmEarlyWarnings,
          // Both spellings: the raw token and the label the chips and panel render, so
          // searching "broken promise" finds what `broken_promise` scored.
          ...getAiSignalTokens(row).map(formatAiSignalLabel),
          ...row.authorityKeywords,
          ...row.tags,
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearchTerm);
      const matchesStatus = matchesSelectedValues(statusFilter, row.ticketStatus);
      const matchesRiskTier = matchesSelectedValues(riskTierFilter, row.riskTier);
      const matchesTeam = matchesTeamFilter(teamFilter, row.team);
      const matchesTeamLeader = matchesTeamFilter(teamLeaderFilter, row.teamLeader);
      const matchesJourneyPhase = matchesSelectedValues(journeyPhaseFilter, row.journeyPhase);

      return (
        matchesSearch &&
        matchesStatus &&
        matchesRiskTier &&
        matchesTeam &&
        matchesTeamLeader &&
        matchesJourneyPhase &&
        matchesTimeOpenFilter(row, timeOpenFilter) &&
        matchesAiSignalFilter(row, aiSignalFilter)
      );
    });
  }, [
    aiSignalFilter,
    journeyPhaseFilter,
    riskTierFilter,
    rows,
    searchTerm,
    statusFilter,
    teamFilter,
    teamLeaderFilter,
    timeOpenFilter,
  ]);

  const rowsBeforeSignalFilter = useMemo(
    () =>
      categoryFilter === ALL
        ? rowsBeforeCategoryFilter
        : rowsBeforeCategoryFilter.filter((row) => getCategoryLabel(row) === categoryFilter),
    [categoryFilter, rowsBeforeCategoryFilter],
  );

  const visibleRows = useMemo(
    () => rowsBeforeSignalFilter.filter((row) => matchesSignalFilter(row, signalFilters)),
    [rowsBeforeSignalFilter, signalFilters],
  );

  const columnGroups = useMemo(
    () =>
      buildCxTicketColumnGroups({
        onOpenTicket: setActiveTicketId,
        onWatchTicket: (ticketId) => watchTicket.mutate({ ticketId }),
        isWatchPending: watchTicket.isPending,
      }),
    [watchTicket],
  );
  const columns = useMemo(() => columnGroups.flatMap((group) => group.columns), [columnGroups]);

  const visibleColumnFields = columns
    .filter((column) => !hiddenColumnFields.includes(column.field))
    .map((column) => column.field);
  const isShowingExtraColumns = visibleColumnFields.some((field) => CX_DEFAULT_HIDDEN_COLUMN_FIELDS.includes(field));
  const columnVisibilityModel = hiddenColumnFields.reduce<Record<string, boolean>>((visibilityModel, field) => {
    visibilityModel[field] = false;

    return visibilityModel;
  }, {});

  const exportVisibleRows = () => {
    const exportColumns: Array<{
      field: keyof CxTicketRow;
      header: string;
      getValue?: (row: CxTicketRow) => unknown;
    }> = [
      { field: 'id', header: 'Ticket ID' },
      { field: 'subject', header: 'Subject' },
      { field: 'ticketStatus', header: 'Ticket Status' },
      { field: 'riskTier', header: 'Risk Tier' },
      { field: 'isNew', header: 'New' },
      { field: 'tierChange', header: 'Tier Change' },
      { field: 'previousTier', header: 'Tier Yesterday' },
      { field: 'watcherName', header: 'Watching' },
      { field: 'watcher', header: 'Watching Since', getValue: (row) => row.watcher?.createdAt ?? '' },
      { field: 'isWatchedByMe', header: 'Watched By Me' },
      { field: 'commentCount', header: 'Comments' },
      { field: 'recentCommentCount', header: 'Comments (Last Hour)' },
      {
        field: 'lastComment',
        header: 'Last Comment',
        getValue: (row) => (row.lastComment ? `${row.lastComment.authorName} at ${row.lastComment.createdAt}` : ''),
      },
      { field: 'riskScore', header: 'Risk Score' },
      { field: 'hardScore', header: 'Hard Score' },
      { field: 'llmScore', header: 'LLM Score' },
      { field: 'divergent', header: 'Divergent' },
      { field: 'percentile', header: 'Percentile' },
      { field: 'criticalRank', header: 'Critical Rank' },
      { field: 'hasLlmAssessment', header: 'Has LLM Assessment' },
      { field: 'llmTrajectory', header: 'Trajectory' },
      { field: 'llmConfidence', header: 'LLM Confidence' },
      { field: 'llmReasoning', header: 'LLM Reasoning' },
      { field: 'llmRiskFactors', header: 'LLM Risk Factors' },
      { field: 'llmEarlyWarnings', header: 'LLM Early Warnings' },
      { field: 'resolutionAccepted', header: 'Resolution Accepted' },
      { field: 'classificationScore', header: 'Classification Risk (30)' },
      { field: 'classificationSource', header: 'Classification Source' },
      { field: 'classificationLift', header: 'Classification Lift' },
      { field: 'riskFlagScore', header: 'Risk Flags (30)' },
      { field: 'matchedRiskFlags', header: 'Matched Risk Flags' },
      { field: 'keywordSignalScore', header: 'Keyword Signals (30)' },
      { field: 'matchedKeywordSignals', header: 'Matched Keyword Signals' },
      { field: 'legalHold', header: 'Legal Hold' },
      { field: 'reassignPoints', header: 'Reassign Points' },
      { field: 'agingPoints', header: 'Aging Points' },
      { field: 'awaitingReviewPoints', header: 'Awaiting Review Points' },
      { field: 'requesterWaitPoints', header: 'Customer Wait Points' },
      { field: 'staleContactPoints', header: 'Stale Contact Points' },
      { field: 'internalCommentPoints', header: 'Internal Comment Points' },
      { field: 'reopenPoints', header: 'Reopen Points' },
      { field: 'operationalRiskScore', header: 'Operational (20)' },
      { field: 'inStayPoints', header: 'In-stay Points' },
      { field: 'upcomingCheckInPoints', header: 'Upcoming Check-in Points' },
      { field: 'recentStayPoints', header: 'Recent Stay Points' },
      { field: 'ltvPoints', header: 'LTV Points' },
      { field: 'societePoints', header: 'Société Points' },
      { field: 'luxPlusPoints', header: 'Lux+ Points' },
      { field: 'customerRiskScore', header: 'Customer Risk (15)' },
      { field: 'created', header: 'Created' },
      { field: 'lastUpdated', header: 'Last Updated' },
      { field: 'statusUpdated', header: 'Status Updated' },
      { field: 'team', header: 'Team' },
      { field: 'assignee', header: 'Assignee' },
      { field: 'teamLeader', header: 'Team Leader' },
      { field: 'ticketReason', header: 'Ticket Reason' },
      { field: 'subcategory', header: 'Subcategory' },
      { field: 'triageCategory', header: 'Triage Category' },
      { field: 'priority', header: 'Priority' },
      { field: 'tags', header: 'Tags' },
      { field: 'replies', header: 'Public Replies' },
      { field: 'internalComments', header: 'Internal Comments' },
      { field: 'agentsTouched', header: 'Agents Touched' },
      { field: 'reassignCount', header: 'Reassigns' },
      { field: 'reopenCount', header: 'Reopens' },
      { field: 'requesterWaitMinutes', header: 'Customer Waiting (Minutes)' },
      { field: 'caseAgeDays', header: 'Case Age (Days)' },
      { field: 'hoursSinceOpen', header: 'Hours Since Open' },
      { field: 'hoursAwaitingReview', header: 'Time Awaiting Review (Hours)' },
      { field: 'hoursSinceLastContact', header: 'Since Last Contact (Hours)' },
      { field: 'lastAgentReplyAt', header: 'Last Agent Reply' },
      { field: 'sentiment', header: 'Sentiment' },
      { field: 'sentimentBucket', header: 'Sentiment Bucket' },
      { field: 'journeyPhase', header: 'Journey Phase' },
      { field: 'checkInDays', header: 'Days Until Check-in' },
      { field: 'daysSinceCheckIn', header: 'Days Since Check-in' },
      { field: 'currentStayCheckInDate', header: 'Current Stay Check-in' },
      { field: 'currentStayCheckOutDate', header: 'Current Stay Check-out' },
      { field: 'nextCheckInDate', header: 'Next Check-in' },
      { field: 'nextCheckOutDate', header: 'Next Check-out' },
      { field: 'lastCheckOutDate', header: 'Last Check-out' },
      { field: 'daysSinceLastCheckOut', header: 'Days Since Last Check-out' },
      { field: 'societeTier', header: 'Société Tier' },
      { field: 'luxPlusMember', header: 'Lux+ Member' },
      { field: 'customerLifetimeValue', header: 'Customer LTV' },
      { field: 'ltvBand', header: 'LTV Band' },
      { field: 'authorityKeywords', header: 'Authority Keywords' },
      { field: 'authorityConfirmed', header: 'Authority Confirmed' },
    ];
    const escapeCsv = (value: unknown) => {
      let normalizedValue = '';

      if (Array.isArray(value)) {
        normalizedValue = value.join('; ');
      } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        normalizedValue = value.toString();
      } else if (value !== null && value !== undefined) {
        normalizedValue = JSON.stringify(value) ?? '';
      }

      return `"${normalizedValue.replace(/"/g, '""')}"`;
    };
    const csv = [
      exportColumns.map((column) => escapeCsv(column.header)).join(','),
      ...visibleRows.map((row) =>
        exportColumns.map((column) => escapeCsv(column.getValue ? column.getValue(row) : row[column.field])).join(','),
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'cx-risk-executive-overview.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const visibleTicketCount = visibleRows.length;
  const signalTicketCounts = useMemo(
    () =>
      Object.fromEntries(
        SIGNAL_FILTER_OPTIONS.map((option) => [
          option.id,
          rowsBeforeSignalFilter.filter((row) => matchesSignalFilter(row, [option.id])).length,
        ]),
      ) as Record<CxSignalFilter, number>,
    [rowsBeforeSignalFilter],
  );
  const getPercentage = (count: number) => (visibleTicketCount === 0 ? 0 : (count / visibleTicketCount) * 100);

  const riskTierColors: Record<RiskTier, string> = {
    Critical: theme.palette.error.main,
    High: theme.palette.warning.main,
    Medium: theme.palette.info.main,
    Low: theme.palette.success.main,
  };

  const toggleRiskTierFilter = (tier: RiskTier) => {
    setRiskSignalsTier(null);
    setRiskTierFilter((currentFilters) => toggleListValue(currentFilters, tier));
  };

  const toggleRiskByTeam = (team: string) => {
    setTeamFilter((currentFilters) => toggleListValue(currentFilters, team));
    setRiskSignalsTier(null);
  };

  const toggleSignalFilter = (signalFilter: CxSignalFilter) => {
    setSignalFilters((currentFilters) =>
      currentFilters.includes(signalFilter)
        ? currentFilters.filter((currentFilter) => currentFilter !== signalFilter)
        : [...currentFilters, signalFilter],
    );
  };

  const riskTierDistribution = useMemo(
    () => RISK_TIERS.map((tier) => ({ tier, value: visibleRows.filter((row) => row.riskTier === tier).length })),
    [visibleRows],
  );

  /**
   * Risk tier composition per Zendesk group. This is the question the overview panel exists to
   * answer — where the risk actually sits — and unlike creation date it is not an input to the
   * score, so it adds information rather than restating the formula.
   */
  const riskByTeam = useMemo(() => {
    const byTeam = new Map<string, Record<RiskTier, number> & { team: string; total: number; atRisk: number }>();

    visibleRows.forEach((row) => {
      const team = row.team ?? 'Unassigned';
      const entry = byTeam.get(team) ?? { team, total: 0, atRisk: 0, Critical: 0, High: 0, Medium: 0, Low: 0 };

      entry[row.riskTier] += 1;
      entry.total += 1;

      if (AT_RISK_TIERS.includes(row.riskTier)) {
        entry.atRisk += 1;
      }

      byTeam.set(team, entry);
    });

    return Array.from(byTeam.values())
      .sort((left, right) => right.atRisk - left.atRisk || right.total - left.total)
      .slice(0, MAX_TEAMS_CHARTED);
  }, [visibleRows]);

  const summaryCards = RISK_TIERS.map((tier) => {
    const count = riskTierDistribution.find((entry) => entry.tier === tier)?.value ?? 0;

    return {
      tier,
      label: tier,
      formattedValue: count.toLocaleString('en-AU'),
      detail: `${getPercentage(count).toFixed(1)}% of the open tickets`,
    };
  });

  const categoryTrendRows = useMemo(() => {
    const categoryCounts = new Map<string, number>();
    // Category trends only track Critical and High tickets, and skip tickets with no category so the
    // "Uncategorised" bucket does not crowd out real categories.
    const trendRows = rowsBeforeCategoryFilter.filter(
      (row) =>
        AT_RISK_TIERS.includes(row.riskTier) &&
        getCategoryLabel(row) !== UNCATEGORISED_LABEL &&
        (categoryFilter === ALL || getCategoryLabel(row) === categoryFilter) &&
        matchesSignalFilter(row, signalFilters),
    );

    trendRows.forEach((row) => {
      const category = getCategoryLabel(row);
      categoryCounts.set(category, (categoryCounts.get(category) ?? 0) + 1);
    });

    return Array.from(categoryCounts.entries())
      .map(([category, current]) => ({ category, current }))
      .sort((left, right) => right.current - left.current)
      .slice(0, MAX_CATEGORY_TREND_ROWS);
  }, [categoryFilter, rowsBeforeCategoryFilter, signalFilters]);

  const riskTierDrivers = useMemo(
    () =>
      RISK_TIERS.reduce<Record<RiskTier, Array<RiskDriver>>>(
        (drivers, tier) => {
          drivers[tier] = buildRiskDrivers(visibleRows.filter((row) => row.riskTier === tier));

          return drivers;
        },
        { Critical: [], High: [], Medium: [], Low: [] },
      ),
    [visibleRows],
  );

  const selectedRiskTier: RiskTier | null = riskTierFilter.length === 1 ? riskTierFilter[0] : riskSignalsTier;
  const riskTierComparisonRows = RISK_TIERS.map((tier) => ({
    tier,
    count: visibleRows.filter((row) => row.riskTier === tier).length,
    drivers: riskTierDrivers[tier],
  }));
  const selectedRiskTierTicketCount =
    selectedRiskTier === null ? 0 : (riskTierComparisonRows.find((row) => row.tier === selectedRiskTier)?.count ?? 0);

  const renderRiskDrivers = (drivers: Array<RiskDriver>, totalCount: number, color: string) => {
    if (drivers.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No matching tickets in this view.
        </Typography>
      );
    }

    const signalRows = drivers.map((driver) => ({
      ...driver,
      percentage: totalCount === 0 ? 0 : (driver.count / totalCount) * 100,
    }));

    return (
      <Stack spacing={1.5}>
        {signalRows.map((driver) => {
          const isRuleDriver = driver.type === 'Rule';
          const isSelected = isRuleDriver ? categoryFilter === driver.query : aiSignalFilter.includes(driver.query);
          // Clicking a driver ticks the matching filter in the bar at the top, so the panel and
          // the filters can never tell different stories about what the grid is showing.
          const toggleDriver = () =>
            isRuleDriver
              ? setCategoryFilter((current) => (current === driver.query ? ALL : driver.query))
              : setAiSignalFilter((current) => toggleListValue(current, driver.query));

          return (
            <Box
              key={`${driver.type}-${driver.label}`}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              title={`Show only the tickets behind ${driver.label}`}
              onClick={toggleDriver}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleDriver();
                }
              }}
              sx={{
                width: '100%',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ width: '100%', minWidth: 0 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    flex: { xs: '0 0 auto', sm: '0 0 45%', md: '0 0 38%' },
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: 0,
                  }}
                >
                  <Chip label={driver.type} size="small" variant="outlined" />
                  <Typography noWrap title={driver.label} sx={{ minWidth: 0 }}>
                    {driver.label}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(driver.percentage, 100)}
                  sx={{
                    flex: { xs: '0 0 auto', sm: 1 },
                    width: { xs: '100%', sm: 'auto' },
                    height: 8,
                    borderRadius: 4,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: { xs: 0, sm: 205 },
                    width: { xs: '100%', sm: 'auto' },
                    textAlign: { xs: 'left', sm: 'right' },
                  }}
                >
                  {driver.count} / {totalCount} ({driver.percentage.toFixed(1)}%) · avg {driver.averageScore.toFixed(0)}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setRiskTierFilter([]);
    setRiskSignalsTier(null);
    setTeamFilter([]);
    setTeamLeaderFilter([]);
    setJourneyPhaseFilter([]);
    setTimeOpenFilter(null);
    setCategoryFilter(ALL);
    setSignalFilters([]);
    setAiSignalFilter([]);
  };

  const noScoringRun = (error as { status?: number } | null)?.status === 404;

  const newCount = report?.meta.newCount;
  const newSentence =
    newCount == null
      ? null
      : newCount === 0
        ? 'No tickets new to High/Critical'
        : `${newCount} ${newCount === 1 ? 'ticket' : 'tickets'} new to High/Critical`;
  const newSentenceSuffix = report?.meta.baseline
    ? ` since ${formatShortDateTime(report.meta.baseline.computedAt)}`
    : ' — first day of scoring, no earlier run to compare';
  // svc-support builds without watching send no `commentCount` at all — then there is nothing to report.
  const supportsWatching = report?.tickets.some((ticket) => ticket.commentCount !== undefined) ?? false;
  const unwatchedHighCritical = rows.filter(
    (row) => (row.riskTier === 'Critical' || row.riskTier === 'High') && row.watcher === null,
  ).length;
  const unwatchedSentence = !supportsWatching
    ? null
    : unwatchedHighCritical === 0
      ? 'every High/Critical ticket has someone watching'
      : `${unwatchedHighCritical} High/Critical ${unwatchedHighCritical === 1 ? 'ticket has' : 'tickets have'} no one watching`;

  // Placed after every hook so the hook order is identical for allowed and blocked users.
  if (!hasAccess) {
    return (
      <Container>
        <Helmet>
          <title>CX dashboard</title>
        </Helmet>

        <PageHeader title="CX dashboard" />
        <Alert severity="warning">
          <AlertTitle>You do not have access to the CX dashboard</AlertTitle>
          This dashboard is in limited rollout while the ticket risk scoring model is validated. Ask the CX engineering
          team if you need access.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Helmet>
          <title>CX dashboard</title>
        </Helmet>

        <PageHeader
          title="Zendesk Ticket Risk Overview"
          subtitle={
            report ? (
              <Box component="span" sx={{ typography: 'body2', color: 'text.secondary' }}>
                Data as of {formatDateTime(report.meta.dataAsOf)} · Score calculated at{' '}
                {formatDateTime(report.meta.computedAt)}
              </Box>
            ) : undefined
          }
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="nowrap"
            sx={{
              alignSelf: 'flex-end',
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              '& .MuiButton-root': {
                flex: { xs: 1, sm: '0 0 auto' },
                minWidth: 0,
                px: { xs: 1, sm: 2 },
                whiteSpace: 'nowrap',
              },
            }}
          >
            <Button variant="outlined" onClick={exportVisibleRows} disabled={visibleTicketCount === 0}>
              Export
            </Button>
            <Button variant="contained" color="primary" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button variant="text" onClick={clearFilters}>
              Clear filters
            </Button>
          </Stack>
        </PageHeader>

        {noScoringRun && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>No scoring run yet</AlertTitle>
            svc-support has not persisted a ticket risk scoring run. Run the ticket-risk-scoring job, then refresh.
          </Alert>
        )}

        {error && !noScoringRun && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Could not load the ticket risk report</AlertTitle>
            {error.message}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <MultiFilterSelect
            id="cx-team-filter-label"
            label="Team"
            value={teamFilter}
            options={teamOptions}
            minWidth={160}
            onChange={setTeamFilter}
          />
          <MultiFilterSelect
            id="cx-team-leader-filter-label"
            label="Team Leader"
            value={teamLeaderFilter}
            options={teamLeaderOptions}
            minWidth={160}
            onChange={setTeamLeaderFilter}
          />
          <MultiFilterSelect
            id="cx-status-filter-label"
            label="Ticket Status"
            value={statusFilter}
            options={statusOptions}
            minWidth={220}
            onChange={setStatusFilter}
          />
          <MultiFilterSelect
            id="cx-risk-tier-filter-label"
            label="Risk Tier"
            value={riskTierFilter}
            options={riskTierOptions}
            minWidth={150}
            onChange={(selectedTiers) => {
              setRiskSignalsTier(null);
              setRiskTierFilter(selectedTiers);
            }}
          />
          <MultiFilterSelect
            id="cx-journey-phase-filter-label"
            label="Journey Phase"
            value={journeyPhaseFilter}
            options={journeyPhaseOptions}
            minWidth={160}
            onChange={setJourneyPhaseFilter}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="cx-time-open-filter-label" shrink>
              Time Open
            </InputLabel>
            <Select
              labelId="cx-time-open-filter-label"
              displayEmpty
              notched
              value={timeOpenFilter ?? ''}
              label="Time Open"
              onChange={(event) =>
                setTimeOpenFilter(event.target.value ? (Number(event.target.value) as TimeOpenFilter) : null)
              }
            >
              <MenuItem value="">{ALL}</MenuItem>
              {TIME_OPEN_FILTER_DAYS.map((days) => (
                <MenuItem key={days} value={days}>
                  {days}+ days
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MultiFilterSelect
            id="cx-signal-filter-label"
            label="Signal"
            value={signalFilters}
            options={SIGNAL_FILTER_OPTIONS.map((option) => option.id)}
            optionLabels={SIGNAL_FILTER_LABELS}
            minWidth={220}
            onChange={setSignalFilters}
          />
          {aiSignalOptions.length > 0 && (
            <MultiFilterSelect
              id="cx-ai-signal-filter-label"
              label="AI Signal"
              value={aiSignalFilter}
              options={aiSignalOptions}
              optionLabels={aiSignalLabels}
              minWidth={220}
              onChange={setAiSignalFilter}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            mb: 2,
            alignItems: 'stretch',
            minHeight: { xs: 'auto', md: DASHBOARD_PANEL_HEIGHT },
          }}
        >
          <Card variant="outlined" sx={{ height: { xs: 'auto', md: DASHBOARD_PANEL_HEIGHT } }}>
            <CardContent
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}
            >
              <Typography variant="h6" fontWeight={700}>
                Risk Tier Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Risk score bands: Critical = the top third of High-band tickets, High = 70–100, Medium = 40–69, Low =
                0–39.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ flex: 1, mt: 1 }}>
                <Box
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    flex: { sm: 1 },
                    minWidth: 0,
                    height: 240,
                    position: 'relative',
                    mx: { sm: -0.5 },
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={riskTierDistribution}
                        dataKey="value"
                        nameKey="tier"
                        innerRadius="58%"
                        outerRadius="94%"
                        paddingAngle={2}
                        onClick={(entry) => {
                          if (entry && typeof entry.name === 'string') {
                            toggleRiskTierFilter(entry.name as RiskTier);
                          }
                        }}
                      >
                        {riskTierDistribution.map((entry) => (
                          <Cell key={entry.tier} fill={riskTierColors[entry.tier]} cursor="pointer" />
                        ))}
                      </Pie>
                      <Tooltip wrapperStyle={{ zIndex: 3 }} formatter={(value, name) => [value, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                  >
                    <Typography variant="h3" fontWeight={700}>
                      {visibleTicketCount.toLocaleString('en-AU')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Open Tickets
                    </Typography>
                  </Stack>
                </Box>
                <Stack
                  spacing={0.5}
                  sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 }, minWidth: 0, pr: { sm: 1 } }}
                >
                  {riskTierDistribution.map((entry) => (
                    <Button
                      key={entry.tier}
                      variant="text"
                      onClick={() => toggleRiskTierFilter(entry.tier)}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', minHeight: 34, py: 0.25, px: 0.5 }}
                    >
                      <Box sx={{ width: 10, height: 10, flexShrink: 0, bgcolor: riskTierColors[entry.tier], mr: 1 }} />
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1, textAlign: 'left' }}>
                        {entry.tier}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ mr: 1, whiteSpace: 'nowrap' }}>
                        {entry.value.toLocaleString('en-AU')} tickets
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 42, textAlign: 'right' }}>
                        {getPercentage(entry.value).toFixed(1)}%
                      </Typography>
                    </Button>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              alignContent: 'stretch',
              height: { xs: 'auto', md: DASHBOARD_PANEL_HEIGHT },
            }}
          >
            {summaryCards.map((card) => (
              <Card
                key={card.label}
                variant="outlined"
                onClick={() => toggleRiskTierFilter(card.tier)}
                sx={{ height: { xs: 'auto', sm: '100%' }, cursor: 'pointer' }}
              >
                <CardContent
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 0.75,
                    p: 1.5,
                    '&:last-child': { pb: 1.5 },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: riskTierColors[card.tier] }} />
                    <Typography variant="subtitle1" color="text.primary" fontWeight={700}>
                      {card.label}
                    </Typography>
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {card.formattedValue}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.detail}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ mb: 2, p: 2, height: { xs: 'auto', md: 295 } }}>
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Top Risk Signals by Risk Tier
              </Typography>
              <Button
                size="small"
                variant="text"
                aria-hidden={!selectedRiskTier}
                sx={{ visibility: selectedRiskTier ? 'visible' : 'hidden' }}
                onClick={() => {
                  setRiskSignalsTier(null);
                  setRiskTierFilter([]);
                }}
              >
                View all tiers
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Comparison across all risk tiers. Select a tier to focus this panel and expand its top signals.
            </Typography>
          </Stack>
          {selectedRiskTier ? (
            renderRiskDrivers(
              riskTierDrivers[selectedRiskTier],
              selectedRiskTierTicketCount,
              riskTierColors[selectedRiskTier],
            )
          ) : (
            <Table
              size="small"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': { py: 1 },
                '& .MuiTableBody-root .MuiTableRow:last-child .MuiTableCell-root': { borderBottom: 0 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: { xs: '30%', sm: '25%' } }}>Risk Tier</TableCell>
                  <TableCell sx={{ width: { xs: '70%', sm: '75%' } }}>Leading risk signals</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {riskTierComparisonRows.map((row) => (
                  <TableRow
                    key={row.tier}
                    hover={row.count > 0}
                    onClick={row.count > 0 ? () => setRiskSignalsTier(row.tier) : undefined}
                    sx={{
                      cursor: row.count > 0 ? 'pointer' : 'default',
                      '&:last-child .MuiTableCell-root': { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: riskTierColors[row.tier] }} />
                        <Typography variant="body2" fontWeight={600}>
                          {row.tier}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                      {row.drivers.length === 0 ? (
                        <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No matching tickets
                          </Typography>
                        </Box>
                      ) : (
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ width: '100%', minWidth: 0 }}
                        >
                          {row.drivers.map((driver) => (
                            <Box
                              key={`${row.tier}-${driver.type}-${driver.label}`}
                              sx={{
                                flex: { sm: '1 1 130px' },
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { xs: 0, sm: 120 },
                                maxWidth: '100%',
                                overflow: 'hidden',
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ width: '100%', minWidth: 0 }}
                              >
                                <Typography
                                  variant="caption"
                                  noWrap
                                  title={driver.label}
                                  sx={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {driver.label}
                                </Typography>
                                <Typography variant="caption" fontWeight={700} sx={{ flexShrink: 0 }}>
                                  {driver.count}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min((driver.count / row.count) * 100, 100)}
                                sx={{
                                  mt: 0.25,
                                  height: 5,
                                  borderRadius: 3,
                                  bgcolor: 'action.hover',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor: riskTierColors[row.tier],
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Paper
              variant="outlined"
              sx={{
                flex: 1,
                minHeight: { xs: 'auto', md: DASHBOARD_PANEL_HEIGHT },
                display: 'flex',
                flexDirection: 'column',
                p: 1.5,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 32 }}>
                <Typography variant="h6" fontWeight={700}>
                  Risk by Team
                </Typography>
                {teamFilter.length > 0 && (
                  <Button size="small" variant="text" onClick={() => setTeamFilter([])}>
                    View all teams
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Tier composition by Zendesk group, ranked by the combined number of Critical and High-risk tickets.
              </Typography>
              <Box sx={{ flex: 1, minHeight: 260, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={riskByTeam}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
                    onClick={(state) => {
                      const team = (state as { activeLabel?: string } | undefined)?.activeLabel;

                      if (team) {
                        toggleRiskByTeam(team);
                      }
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="team"
                      width={160}
                      tick={(props) => {
                        const { x, y, payload } = props as {
                          x?: number;
                          y?: number;
                          payload?: { value?: string };
                        };
                        const team = payload?.value ?? '';

                        return (
                          <text
                            x={x}
                            y={y}
                            dy={4}
                            textAnchor="end"
                            fill={theme.palette.text.primary}
                            fontSize={12}
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleRiskByTeam(team)}
                          >
                            {team}
                          </text>
                        );
                      }}
                    />
                    <Tooltip
                      wrapperStyle={{ zIndex: 3 }}
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) {
                          return null;
                        }

                        const team = payload[0]?.payload?.team;
                        const total = payload[0]?.payload?.total;
                        const orderedPayload = [...payload].sort(
                          (left, right) =>
                            RISK_TIERS.indexOf(String(left.dataKey) as RiskTier) -
                            RISK_TIERS.indexOf(String(right.dataKey) as RiskTier),
                        );

                        return (
                          <Paper elevation={3} sx={{ p: 1 }}>
                            <Typography variant="body2" fontWeight={700}>
                              {team}
                            </Typography>
                            {orderedPayload.map((entry) => (
                              <Stack key={String(entry.dataKey)} direction="row" spacing={1} alignItems="center">
                                <Box sx={{ width: 8, height: 8, bgcolor: entry.color }} />
                                <Typography variant="caption">
                                  {entry.name}: {entry.value}
                                </Typography>
                              </Stack>
                            ))}
                            <Typography variant="caption" fontWeight={700}>
                              Total: {total}
                            </Typography>
                          </Paper>
                        );
                      }}
                    />
                    <Legend
                      content={() => (
                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ pt: 1 }}>
                          {RISK_TIERS.map((tier) => (
                            <Stack key={tier} direction="row" spacing={0.75} alignItems="center">
                              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: riskTierColors[tier] }} />
                              <Typography variant="body2">{tier}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    />
                    {RISK_TIERS.map((tier) => (
                      <Bar key={tier} dataKey={tier} stackId="tier" fill={riskTierColors[tier]} cursor="pointer" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
            <Paper variant="outlined" sx={{ flex: 1, minHeight: { xs: 'auto', md: DASHBOARD_PANEL_HEIGHT }, p: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 32 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Category Trends
                </Typography>
                {categoryFilter !== ALL && (
                  <Button size="small" variant="text" onClick={() => setCategoryFilter(ALL)}>
                    View all categories
                  </Button>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Critical and High-risk open ticket volume by category in the filtered view.
              </Typography>
              {categoryTrendRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No category data is available for this view.
                </Typography>
              ) : (
                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': { py: 0.75 },
                    '& .MuiTableBody-root .MuiTableRow:last-child .MuiTableCell-root': { borderBottom: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: { xs: '50%', sm: '60%' } }}>Category</TableCell>
                      <TableCell align="right" sx={{ width: { xs: '50%', sm: '40%' }, whiteSpace: 'nowrap' }}>
                        Tickets
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryTrendRows.map((trend) => (
                      <TableRow
                        key={trend.category}
                        hover
                        onClick={() => {
                          setCategoryFilter((currentCategory) =>
                            currentCategory === trend.category ? ALL : trend.category,
                          );
                          setSearchTerm('');
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:last-child .MuiTableCell-root': { borderBottom: 0 },
                        }}
                      >
                        <TableCell>{trend.category}</TableCell>
                        <TableCell align="right">{trend.current}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
              Signals to Watch
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ width: { xs: '100%', md: 'auto' }, '& .MuiChip-root': { maxWidth: '100%' } }}
            >
              {SIGNAL_FILTER_OPTIONS.map((option) => (
                <Chip
                  key={option.id}
                  size="small"
                  label={`${signalTicketCounts[option.id]} ${option.label}`}
                  variant={signalFilters.includes(option.id) ? 'filled' : 'outlined'}
                  clickable
                  onClick={() => toggleSignalFilter(option.id)}
                  onDelete={signalFilters.includes(option.id) ? () => toggleSignalFilter(option.id) : undefined}
                />
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            label="Search tickets"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="ID, subject, team, assignee, tag..."
            size="small"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="cx-columns-filter-label">Show columns</InputLabel>
            <Select
              labelId="cx-columns-filter-label"
              multiple
              value={visibleColumnFields}
              label="Show columns"
              MenuProps={{
                disableAutoFocusItem: true,
                MenuListProps: { autoFocusItem: false, variant: 'menu' },
                PaperProps: { sx: { maxHeight: COLUMN_MENU_MAX_HEIGHT } },
              }}
              onChange={(event) => {
                const selectedFields =
                  typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;

                setHiddenColumnFields(
                  columns.map((column) => column.field).filter((field) => !selectedFields.includes(field)),
                );
              }}
              renderValue={(selected) => `${selected.length} of ${columns.length} columns`}
            >
              {/* Grouped, because the detail columns are only findable when they sit under the
                  heading you would look for them by. */}
              {columnGroups.flatMap((group) => [
                <ListSubheader key={group.id}>{group.label}</ListSubheader>,
                ...group.columns.map((column) => (
                  <MenuItem key={column.field} value={column.field}>
                    <Checkbox checked={visibleColumnFields.includes(column.field)} />
                    <ListItemText primary={column.headerName} />
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
          {isShowingExtraColumns && (
            <Button
              variant="text"
              onClick={() => setHiddenColumnFields(CX_DEFAULT_HIDDEN_COLUMN_FIELDS)}
              sx={{ alignSelf: 'center' }}
            >
              Reset columns
            </Button>
          )}
          {categoryFilter !== ALL && (
            <Chip
              label={`Category: ${categoryFilter}`}
              onDelete={() => setCategoryFilter(ALL)}
              sx={{ alignSelf: 'center' }}
            />
          )}
        </Stack>
      </Container>

      {/* Only the grid goes full width. Nine columns fit an xl bound on paper, but the stacked
          detail lines need the room — at xl the Customer column falls off the right edge. */}
      <Container>
        {(newSentence || unwatchedSentence) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {joinDetails([newSentence && `${newSentence}${newSentenceSuffix}`, unwatchedSentence])}
          </Typography>
        )}

        <Paper variant="outlined">
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={visibleRows}
              columns={columns}
              loading={isLoading}
              columnVisibilityModel={columnVisibilityModel}
              disableColumnSelector
              autoHeight
              // The triage columns stack a value over its detail, so rows need two lines. Autosizing
              // is gone with them: nine flex columns fit the viewport without measuring content, and
              // measuring it was what let one long subcategory widen the column for every row.
              rowHeight={COMPRESSED_ROW_HEIGHT}
              disableRowSelectionOnClick
              onRowClick={(params, event) => {
                const target = event.target as HTMLElement;

                if (target.closest('a,button')) {
                  return;
                }

                setActiveTicketId(params.row.id);
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                sorting: { sortModel: [{ field: 'risk', sort: 'desc' }] },
              }}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
              }}
            />
          </Box>
        </Paper>
      </Container>

      <TicketDetailDialog
        ticket={activeTicket}
        onClose={() => setActiveTicketId(null)}
        currentUserId={user?.id_member ?? null}
      />
    </>
  );
}
